import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { updateCustomerData } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import {
  APIPaths,
  ContactPurpose,
  LeaseApplication,
  PrivateCustomer,
} from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import { formatGermanDateToUTC } from '../../../../utils/sanitize-data';
import WorkContactBankTemplate, {
  WorkContactBankData,
} from '../../templates/kia-flex/work-contact-bank-template';

interface WorkContactBankProps {
  getPrivateCustomer: (
    leaseApp: LeaseApplication | null,
  ) => PrivateCustomer | undefined;
}

const WorkContactBank: React.FC<WorkContactBankProps> = ({
  getPrivateCustomer,
}) => {
  // Lease Application Storage
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Storage
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // Init history hook (used in nav)
  const navigate = useNavigate();

  // Navigation class
  const { nextRoute } = new Navigation();

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Get PrivateCustomer from the lease application
  const privateCustomer = getPrivateCustomer(activeApplication);

  const [loading, setLoading] = useState<boolean>(false);

  // OnSubmit
  const submitData = (data: WorkContactBankData) => {
    setLoading(true);
    Promise.all([
      dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId: activeApplication?.uuid || '',
          inputValue: {
            employment_since: formatGermanDateToUTC(
              data.employment_since as string,
            ),
          },
          path: APIPaths.LIVING_DETAILS,
        }),
      ),
      dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId: activeApplication?.uuid || '',
          inputValue: {
            contact_details: {
              phone_numbers: [
                {
                  value: data.phone_number,
                  purpose: ContactPurpose.CONTACT,
                  data_usage: {
                    answer: data.terms,
                  },
                },
              ],
              emails: [
                {
                  value: data.email,
                  purpose: ContactPurpose.CONTACT,
                },
              ],
            },
          },
          path: APIPaths.CONTACT_DETAILS,
        }),
      ),
      dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId: activeApplication?.uuid || '',
          inputValue: {
            bank_account: {
              account_owner: data.account_owner,
              iban: data.iban,
            },
          },
          path: APIPaths.BANK_ACCOUNT,
        }),
      ),
    ])
      .then(() => {
        setLoading(false);
        navigate(nextRoute());
      })
      .catch(() => {
        setLoading(false);
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: 'Das tut uns leid',
            notificationBody: getText('error_unable_to_process_data'),
          }),
        );
        errorLogging(
          new Error(
            `unable to submit work, contact and bank data to user with application id ${activeApplication?.uuid}`,
          ),
        );
      });
  };

  return (
    <Layout stepStatus heading='Beruf, Kontakt und Bankverbindung'>
      <WorkContactBankTemplate
        tabButton={false}
        onSubmit={submitData}
        privateCustomer={privateCustomer}
        loading={loading}
      />
    </Layout>
  );
};

export default WorkContactBank;
